
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useUser } from '@/hooks/useUser';
import { useOnboarding } from '@/hooks/useOnboarding';

interface UserFormData {
  firstName: string;
  lastName: string;
  jobTitle: string;
  experienceLevel: string;
  goals: string;
}

interface OrganizationFormData {
  organizationName: string;
  industry: string;
  companySize: string;
  location: string;
}

export const useOnboardingDataHydration = () => {
  const { user: authUser } = useAuth();
  const { user: profileUser } = useUser();
  const { organizationStatus } = useOnboarding();
  
  const [userFormData, setUserFormData] = useState<UserFormData>({
    firstName: '',
    lastName: '',
    jobTitle: '',
    experienceLevel: '',
    goals: '',
  });

  const [organizationFormData, setOrganizationFormData] = useState<OrganizationFormData>({
    organizationName: '',
    industry: '',
    companySize: '',
    location: '',
  });

  const [isDataHydrated, setIsDataHydrated] = useState(false);

  // Memoize derived values to prevent unnecessary re-renders
  const hasExistingOrganization = useMemo(() => {
    return organizationStatus?.hasOrganization || false;
  }, [organizationStatus?.hasOrganization]);

  // Memoize the data sources to prevent effect from running on object recreation
  const dataIsReady = useMemo(() => {
    return !!(authUser && profileUser);
  }, [authUser?.id, profileUser?.id]);

  // Use callback to prevent function recreation on every render
  const hydrateData = useCallback(() => {
    if (!authUser || !profileUser || isDataHydrated) {
      return;
    }

    console.log('[useOnboardingDataHydration] Hydrating data for user:', authUser.id);

    // Hydrate user form data from multiple sources
    const hydratedUserData: UserFormData = {
      firstName: profileUser.first_name || 
                authUser.user_metadata?.first_name || 
                authUser.user_metadata?.firstName || '',
      lastName: profileUser.last_name || 
               authUser.user_metadata?.last_name || 
               authUser.user_metadata?.lastName || '',
      jobTitle: profileUser.job_title || '',
      experienceLevel: profileUser.experience_level || '',
      goals: profileUser.goals || '',
    };

    // Hydrate organization data if available
    const hydratedOrgData: OrganizationFormData = {
      organizationName: authUser.user_metadata?.organization_name || 
                       organizationStatus?.organizationName || '',
      industry: '',
      companySize: '',
      location: '',
    };

    console.log('[useOnboardingDataHydration] Setting hydrated data:', {
      userData: hydratedUserData,
      orgData: hydratedOrgData,
      hasOrg: hasExistingOrganization
    });

    setUserFormData(hydratedUserData);
    setOrganizationFormData(hydratedOrgData);
    setIsDataHydrated(true);
  }, [
    authUser?.id,
    authUser?.user_metadata?.first_name,
    authUser?.user_metadata?.firstName,
    authUser?.user_metadata?.last_name, 
    authUser?.user_metadata?.lastName,
    authUser?.user_metadata?.organization_name,
    profileUser?.id,
    profileUser?.first_name,
    profileUser?.last_name,
    profileUser?.job_title,
    profileUser?.experience_level,
    profileUser?.goals,
    organizationStatus?.organizationName,
    hasExistingOrganization,
    isDataHydrated
  ]);

  // Only run hydration when data is ready and not already hydrated
  useEffect(() => {
    if (dataIsReady && !isDataHydrated) {
      hydrateData();
    }
  }, [dataIsReady, isDataHydrated, hydrateData]);

  return {
    userFormData,
    organizationFormData,
    isDataHydrated,
    hasExistingOrganization,
    setUserFormData,
    setOrganizationFormData,
  };
};
